import { Button } from '@nextui-org/react'
import { motion } from 'framer-motion'
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

import { Icons } from '@/components/icons'

export interface TourStep {
    targetId: string
    title: string
    content: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
}

interface HomologationTourProps {
    isOpen: boolean
    onClose: () => void
    steps: TourStep[]
    onComplete?: (dontShowAgain: boolean) => void
}

// eslint-disable-next-line max-lines-per-function
export const HomologationTour: React.FC<HomologationTourProps> = ({
    isOpen,
    onClose,
    steps,
    onComplete
}) => {
    const [currentStep, setCurrentStep] = useState(0)
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
    const [isCalculating, setIsCalculating] = useState(false)
    const [dontShowAgain, setDontShowAgain] = useState(false)

    // Reset step when opening
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0)
            setDontShowAgain(false)
        }
    }, [isOpen])

    // Calculate position of current target
    useEffect(() => {
        if (!isOpen) {
            return
        }

        const updatePosition = () => {
            const step = steps[currentStep]

            if (!step) {
                return
            }

            const element = document.getElementById(step.targetId)

            if (element) {
                // Scroll element into view if needed
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center'
                })

                // Wait a bit for scroll to finish or just update rect immediately and periodically
                setIsCalculating(true)
                setTimeout(() => {
                    const rect = element.getBoundingClientRect()

                    setTargetRect(rect)
                    setIsCalculating(false)
                }, 300) // Small delay for scroll
            } else {
                // If element not found, maybe skip or wait?
                console.warn(`Tour target ${step.targetId} not found`)
            }
        }

        updatePosition()

        // Update on resize
        window.addEventListener('resize', updatePosition)

        return () => window.removeEventListener('resize', updatePosition)
    }, [currentStep, isOpen, steps])

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        } else {
            onComplete?.(dontShowAgain)
            onClose()
        }
    }

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    if (!isOpen || !targetRect) {
        return null
    }

    // Portal to body to ensure it's on top of everything
    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-hidden">
            {/* Backdrop with hole */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <defs>
                    <mask id="tour-mask">
                        <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="white"
                        />
                        <rect
                            x={targetRect.left - 5}
                            y={targetRect.top - 5}
                            width={targetRect.width + 10}
                            height={targetRect.height + 10}
                            rx="8"
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0.6)"
                    mask="url(#tour-mask)"
                />
            </svg>

            {/* Highlight Border */}
            <motion.div
                className="absolute border-2 border-emerald-500 rounded-lg pointer-events-none shadow-[0_0_20px_rgba(16,185,129,0.5)]"
                initial={false}
                animate={{
                    left: targetRect.left - 5,
                    top: targetRect.top - 5,
                    width: targetRect.width + 10,
                    height: targetRect.height + 10
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />

            {/* Popover */}
            <motion.div
                className="absolute bg-white p-5 rounded-xl shadow-2xl max-w-sm w-full pointer-events-auto"
                initial={{ opacity: 0, y: 10 }}
                animate={{
                    opacity: 1,
                    y: 0,
                    left: getPopoverPosition(
                        targetRect,
                        steps[currentStep].placement
                    ).x,
                    top: getPopoverPosition(
                        targetRect,
                        steps[currentStep].placement
                    ).y
                }}
                key={currentStep} // Re-animate on step change
                transition={{ duration: 0.3 }}
            >
                <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg text-gray-800">
                        {steps[currentStep].title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <Icons.Times className="w-5 h-5" />
                    </button>
                </div>

                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    {steps[currentStep].content}
                </p>

                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="dontShowAgain"
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                        />
                        <label
                            htmlFor="dontShowAgain"
                            className="text-xs text-gray-500 cursor-pointer select-none"
                        >
                            No volver a mostrar esta guía
                        </label>
                    </div>

                    <div className="flex justify-between items-center">
                        <div className="flex gap-1">
                            {steps.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        idx === currentStep
                                            ? 'w-6 bg-emerald-500'
                                            : 'w-1.5 bg-gray-200'
                                    }`}
                                />
                            ))}
                        </div>

                        <div className="flex gap-2">
                            {currentStep > 0 && (
                                <Button
                                    size="sm"
                                    variant="light"
                                    onPress={handlePrev}
                                    className="font-medium text-gray-600"
                                >
                                    Anterior
                                </Button>
                            )}
                            <Button
                                size="sm"
                                color="primary"
                                onPress={handleNext}
                                className="bg-emerald-600 text-white font-medium shadow-md hover:bg-emerald-700"
                            >
                                {currentStep === steps.length - 1
                                    ? 'Finalizar'
                                    : 'Siguiente'}
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>,
        document.body
    )
}

function getPopoverPosition(
    target: DOMRect,
    placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom'
) {
    const gap = 15
    const popoverWidth = 320 // Approx width
    const popoverHeight = 200 // Approx height (dynamic but estimating for safe positioning)

    // Default to bottom
    let x = target.left + target.width / 2 - popoverWidth / 2
    let y = target.bottom + gap

    // Adjust if off screen (simple logic)
    if (x < 10) {
        x = 10
    }
    if (x + popoverWidth > window.innerWidth - 10) {
        x = window.innerWidth - popoverWidth - 10
    }

    if (placement === 'top') {
        y = target.top - popoverHeight - gap
    } else if (placement === 'left') {
        x = target.left - popoverWidth - gap
        y = target.top
    } else if (placement === 'right') {
        x = target.right + gap
        y = target.top
    }

    return { x, y }
}
